import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type {
  WarrantyTemplate,
  WarrantyTemplateDocument,
  WarrantyTemplateOption,
  WarrantyCategory,
  WarrantyAttribute,
  WarrantyAttributeValue,
  ProrationFormula,
  ProrationCalculationResult,
  TermsSynthesis,
} from '@/admin/types/warranties'
import type { PaginatedResponse } from '@/shared/types/api'
import { usePermissions } from '@/admin/hooks/use-permissions'

// ── Warranty instances (list) ──────────────────────────────

export interface WarrantyListRow {
  uuid: string
  holding_uuid?: string | null
  consumer_account?: { first_name?: string; last_name?: string } | null
  warranty_template?: { name?: string } | null
  status?: string
  active_from?: string | null
  expires_at?: string | null
  payment_status?: string | null
  remaining_days?: number | null
}

/** Paginated warranty instances at /warranties/. Supports warranty_type,
 * payment_status, purchased_within_days, expiring_within_days, status, search. */
export function useWarranties(params?: Record<string, string>) {
  return useQuery<PaginatedResponse<WarrantyListRow> & { filter_options?: unknown[] }>({
    queryKey: ['warranties', params],
    queryFn: () => api.get('/warranties/', { params }).then((r) => r.data),
  })
}

// ── Warranty Templates ─────────────────────────────────────

export function useWarrantyTemplates(
  search?: string,
  options: { enabled?: boolean } = {},
) {
  // WarrantyTemplateListCreateView enforces `warranty-templates.view` on GET.
  const { can } = usePermissions()
  return useQuery<WarrantyTemplate[]>({
    queryKey: ['warranty-templates', search ?? ''],
    queryFn: () =>
      api
        .get<PaginatedResponse<WarrantyTemplate>>('/warranties/templates/', {
          params: { page_size: 200, ...(search ? { search } : {}) },
        })
        .then(r => r.data.results),
    enabled: (options.enabled ?? true) && can('warranty-templates.view'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useWarrantyTemplate(uuid: string | null) {
  return useQuery<WarrantyTemplate>({
    queryKey: ['warranty-template', uuid],
    queryFn: () => api.get(`/warranties/templates/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export function useCreateWarrantyTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<WarrantyTemplate>) =>
      api.post('/warranties/templates/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-templates'] }),
  })
}

export type WarrantyTemplateUpdate = Partial<WarrantyTemplate> & {
  proration_formula_uuid?: string | null
  warranty_category_uuid?: string | null
}

export function useUpdateWarrantyTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: WarrantyTemplateUpdate }) =>
      api.put(`/warranties/templates/${uuid}/`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.uuid] })
    },
  })
}

export function useDeleteWarrantyTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/warranties/templates/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-templates'] }),
  })
}

export function useCloneWarrantyTemplate() {
  const qc = useQueryClient()
  return useMutation<WarrantyTemplate, Error, string>({
    mutationFn: (uuid: string) =>
      api.post(`/warranties/templates/${uuid}/clone/`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-templates'] }),
  })
}

export function useActivateWarrantyTemplate() {
  const qc = useQueryClient()
  return useMutation<WarrantyTemplate, Error, string>({
    mutationFn: (uuid: string) =>
      api
        .put(`/warranties/templates/${uuid}/`, { status: 'ACTIVE' })
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template'] })
    },
  })
}

export function useReorderWarrantyTemplates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { uuid: string; display_order: number }[]) =>
      api.put('/warranties/templates/reorder/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-templates'] }),
  })
}

// ── Warranty Template Options ───────────────────────────────

export function useCreateWarrantyTemplateOption() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      classUuid,
      data,
    }: {
      classUuid: string
      data: Partial<WarrantyTemplateOption>
    }) =>
      api
        .post(`/warranties/templates/${classUuid}/options/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.classUuid] })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useDeleteWarrantyTemplateOption() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      classUuid,
      optionUuid,
    }: {
      classUuid: string
      optionUuid: string
    }) =>
      api.delete(`/warranties/templates/${classUuid}/options/${optionUuid}/`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.classUuid] })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useUpdateWarrantyTemplateOption() {
  const qc = useQueryClient()
  return useMutation<
    WarrantyTemplateOption,
    Error,
    {
      classUuid: string
      optionUuid: string
      data: Partial<WarrantyTemplateOption>
    }
  >({
    mutationFn: ({ classUuid, optionUuid, data }) =>
      api
        .patch(`/warranties/templates/${classUuid}/options/${optionUuid}/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.classUuid] })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useReorderWarrantyTemplateOptions(templateUuid: string) {
  const qc = useQueryClient()
  return useMutation<
    WarrantyTemplateOption[],
    Error,
    { uuid: string; display_order: number }[]
  >({
    mutationFn: (order) =>
      api
        .post(`/warranties/templates/${templateUuid}/options/reorder/`, {
          options: order,
        })
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-template', templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['entity-types'] })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

// ── Warranty Template Documents ──────────────────────────

export interface WarrantyTemplateDocumentWrite {
  label: string
  kind: WarrantyTemplateDocument['kind']
  uploaded_file_uuid?: string | null
  url?: string | null
}

export function useCreateWarrantyTemplateDocument(templateUuid: string) {
  const qc = useQueryClient()
  return useMutation<WarrantyTemplateDocument, Error, WarrantyTemplateDocumentWrite>({
    mutationFn: (data) =>
      api
        .post(`/warranties/templates/${templateUuid}/documents/`, data)
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-template', templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
    },
  })
}

export function useUpdateWarrantyTemplateDocument(templateUuid: string) {
  const qc = useQueryClient()
  return useMutation<
    WarrantyTemplateDocument,
    Error,
    { documentUuid: string; data: Partial<WarrantyTemplateDocumentWrite> & { clear_source?: boolean } }
  >({
    mutationFn: ({ documentUuid, data }) =>
      api
        .patch(
          `/warranties/templates/${templateUuid}/documents/${documentUuid}/`,
          data,
        )
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-template', templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
    },
  })
}

export function useDeleteWarrantyTemplateDocument(templateUuid: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (documentUuid) =>
      api
        .delete(
          `/warranties/templates/${templateUuid}/documents/${documentUuid}/`,
        )
        .then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-template', templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
    },
  })
}

export function useReorderWarrantyTemplateDocuments(templateUuid: string) {
  const qc = useQueryClient()
  return useMutation<
    WarrantyTemplateDocument[],
    Error,
    { uuid: string; display_order: number }[]
  >({
    mutationFn: (order) =>
      api
        .post(
          `/warranties/templates/${templateUuid}/documents/reorder/`,
          { documents: order },
        )
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-template', templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
    },
  })
}

// ── Terms Synthesis ──────────────────────────────────────

export function useGenerateTermsSynthesis() {
  return useMutation<TermsSynthesis, Error, { raw_terms: string }>({
    mutationFn: (data) =>
      api
        .post('/warranties/templates/generate-terms-synthesis/', data)
        .then(r => r.data),
  })
}

// ── Proration Formulas ───────────────────────────────────

export function useProrationFormulas() {
  return useQuery<ProrationFormula[]>({
    queryKey: ['proration-formulas'],
    queryFn: () =>
      api
        .get<PaginatedResponse<ProrationFormula>>('/warranties/proration-formulas/', {
          params: { page_size: 200 },
        })
        .then(r => r.data.results),
  })
}

export function useProrationFormula(uuid: string | null) {
  return useQuery<ProrationFormula>({
    queryKey: ['proration-formula', uuid],
    queryFn: () =>
      api.get(`/warranties/proration-formulas/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export function useCreateProrationFormula() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ProrationFormula>) =>
      api.post('/warranties/proration-formulas/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proration-formulas'] }),
  })
}

export function useUpdateProrationFormula() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<ProrationFormula> }) =>
      api.put(`/warranties/proration-formulas/${uuid}/`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['proration-formulas'] })
      qc.invalidateQueries({ queryKey: ['proration-formula', vars.uuid] })
    },
  })
}

export function useDeleteProrationFormula() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/warranties/proration-formulas/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proration-formulas'] }),
  })
}

export function useCalculateProration() {
  return useMutation<ProrationCalculationResult, Error, Record<string, unknown>>({
    mutationFn: (data) =>
      api
        .post('/warranties/proration-formulas/calculate/', data)
        .then(r => r.data),
  })
}

// ── Warranty Categories ─────────────────────────────────

export function useWarrantyCategories(options: { enabled?: boolean } = {}) {
  return useQuery<WarrantyCategory[]>({
    queryKey: ['warranty-categories'],
    queryFn: () =>
      api
        .get<PaginatedResponse<WarrantyCategory>>('/warranties/categories/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useCreateWarrantyCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<WarrantyCategory>) =>
      api.post('/warranties/categories/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-categories'] }),
  })
}

export function useUpdateWarrantyCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<WarrantyCategory> }) =>
      api.put(`/warranties/categories/${uuid}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-categories'] }),
  })
}

export function useDeleteWarrantyCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/warranties/categories/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-categories'] }),
  })
}

// ── Warranty Attributes ─────────────────────────────────

export function useWarrantyAttributes(templateUuid: string | null) {
  return useQuery<WarrantyAttribute[]>({
    queryKey: ['warranty-attributes', templateUuid],
    queryFn: () =>
      api.get(`/warranties/templates/${templateUuid}/attributes/`).then(r => r.data),
    enabled: !!templateUuid,
  })
}

export function useCreateWarrantyAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      data,
    }: {
      templateUuid: string
      data: Partial<WarrantyAttribute>
    }) =>
      api
        .post(`/warranties/templates/${templateUuid}/attributes/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-attributes', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.templateUuid] })
    },
  })
}

export function useUpdateWarrantyAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attrUuid,
      data,
    }: {
      templateUuid: string
      attrUuid: string
      data: Partial<WarrantyAttribute>
    }) =>
      api
        .put(`/warranties/templates/${templateUuid}/attributes/${attrUuid}/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-attributes', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.templateUuid] })
    },
  })
}

export function useDeleteWarrantyAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attrUuid,
      force,
    }: {
      templateUuid: string
      attrUuid: string
      force?: boolean
    }) =>
      api.delete(`/warranties/templates/${templateUuid}/attributes/${attrUuid}/`, {
        params: force ? { force: 'true' } : undefined,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-attributes', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.templateUuid] })
    },
  })
}

export function useReorderWarrantyAttributes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributes,
    }: {
      templateUuid: string
      attributes: { uuid: string }[]
    }) =>
      api
        .put(`/warranties/templates/${templateUuid}/attributes/reorder/`, { attributes })
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-attributes', vars.templateUuid] })
    },
  })
}

export function useWarrantyAttributeValues(templateUuid: string | null) {
  return useQuery<WarrantyAttributeValue[]>({
    queryKey: ['warranty-attribute-values', templateUuid],
    queryFn: () =>
      api.get(`/warranties/templates/${templateUuid}/attribute-values/`).then(r => r.data),
    enabled: !!templateUuid,
  })
}

export function useSaveWarrantyAttributeValues() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      data,
    }: {
      templateUuid: string
      data: Record<string, unknown>
    }) =>
      api
        .put(`/warranties/templates/${templateUuid}/attribute-values/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['warranty-attribute-values', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['warranty-templates'] })
      qc.invalidateQueries({ queryKey: ['warranty-template', vars.templateUuid] })
    },
  })
}

// ── Bulk Sync Endpoints (E7-S3) ───────────────────────────

export function useBulkSyncGeoZones(templateUuid: string) {
  const queryClient = useQueryClient()
  return useMutation<
    { created: number; removed: number; unchanged: number },
    Error,
    { geo_zone_ids: string[] }
  >({
    mutationFn: (data) =>
      api
        .put(`/warranties/templates/${templateUuid}/geo-zones/`, data)
        .then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['warranty-template-geo-zones', templateUuid],
      })
      void queryClient.invalidateQueries({
        queryKey: ['warranty-template', templateUuid],
      })
    },
  })
}

export function useWarrantyTemplateGeoZones(templateUuid: string | null) {
  return useQuery<{ uuid: string; geo_zone_uuid: string; geo_zone_name: string; geo_zone_code: string }[]>({
    queryKey: ['warranty-template-geo-zones', templateUuid],
    queryFn: () =>
      api
        .get(`/warranties/templates/${templateUuid}/geo-zones/`)
        .then((r) => r.data),
    enabled: !!templateUuid,
  })
}
